import { Resources } from 'constants/resources';

import { usePermissions } from 'hooks/usePermissions';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider } from 'react-admin';
import { Button, DeleteButton } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { ContextMenu, ContextMenuEntityUserActionsButton } from 'shared/mui/ContextMenu';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { MerchantAccountShowDialogTable } from 'shared/widgets/MerchantAccountShowDialog/MerchantAccountShowDialogTable';
import { MerchantAccount } from 'types/embedded/merchantAccount';
import { Permission } from 'types/embedded/permission';

type Props = {
  open: boolean;
  onEdit: (record: MerchantAccount) => void;
  onDelete: (record: MerchantAccount) => void;
  handleOpenCredentialsDialog: (record: MerchantAccount) => void;
  onClose: () => void;
  merchantAccount?: MerchantAccount;
};

export const MerchantShowAccountsShowDialog: React.FC<Props> = ({
  open,
  onClose,
  onEdit,
  onDelete,
  handleOpenCredentialsDialog,
  merchantAccount,
}) => {
  const translate = useTypedTranslate();
  const { hasResourceAccess, hasResourcePermissions } = usePermissions();

  return (
    <Dialog onClose={onClose} open={open} width="640px">
      <RecordContextProvider value={merchantAccount}>
        <CardHeader
          appendActions={[
            <ContextMenu key="context-menu-button">
              {hasResourceAccess(Resources.UserActions) && (
                <ContextMenuEntityUserActionsButton entityName="merchant_account" />
              )}
            </ContextMenu>,
            <CloseIconButton key="close-button" onClose={onClose} />,
          ]}
          title={translate('app.merchantAccounts.merchantAccountDetails')}
          titleSize="smallBold"
        />
        <CardContent>
          <MerchantAccountShowDialogTable
            handleOpenCredentialsDialog={handleOpenCredentialsDialog}
            merchantAccount={merchantAccount}
            omit={['merchant']}
            onClose={onClose}
          />
        </CardContent>
        <CardActionsButtons
          rightActionsSlot={[
            hasResourcePermissions('merchant_accounts', [Permission.Full, Permission.Delete]) && (
              <DeleteButton
                onClick={() => {
                  onClose();
                  merchantAccount && onDelete(merchantAccount);
                }}
                variant="outlined"
              />
            ),
            hasResourcePermissions('merchant_accounts', [Permission.Full, Permission.Update]) &&
              !merchantAccount?.locked && (
                <Button
                  label={translate('app.merchantAccounts.edit')}
                  onClick={() => {
                    onClose();
                    merchantAccount && onEdit(merchantAccount);
                  }}
                  variant="contained"
                />
              ),
          ]}
        />
      </RecordContextProvider>
    </Dialog>
  );
};
